
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Stack, useMediaQuery } from '@mui/system';
import { Card, Link, Button, useTheme, Typography, IconButton, CardContent } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { EntityLinkType } from 'src/services/common.types';
import { ContactSource } from 'src/services/contacts/contacts.types';
import { ICompanyContactLink } from 'src/services/connections/connections.types';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';
import ContactsNotesModal from 'src/components/custom-tables/contacts-table/contacts-notes-modal';

type Props = {
  row: ICompanyContactLink;
  onRemoveLink?: (contactLink: ICompanyContactLink) => void;
};

export function ContactLinksTableRow({ row, onRemoveLink }: Props) {
  const { t } = useTranslate();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showNotesModal = useBoolean();

  const confirm = useBoolean();

  const handleRemoveContact = () => {
    if (!onRemoveLink) return;

    confirm.onFalse();
    onRemoveLink(row);
  }

  const getViewDetailPath = (data: ICompanyContactLink) => paths.dashboard.contacts.detail(data.contact.id);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardContent>
          <Stack direction="column" alignItems="start" mb={1}>
            <Link
              href={getViewDetailPath(row)}
              component={RouterLink}
            >
              <TruncatedTextField
                text={`${row.contact.first_name} ${row.contact.last_name ?? ''}`}
                mode="characters"
                limit={25}
                showMoreEnabled={false}
                variant="h6"
                fontWeight="bold"
                gutterBottom
                style={{ textTransform: 'capitalize' }}
              />
            </Link>

            {
              (row.contact.account && row.contact.account.organization) && (
                <Typography variant='caption'>
                  {row.contact.account.organization.company_name}
                </Typography>
              )
            }

          </Stack>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {row.contact.email ? (
              <a href={`mailto:${row.contact.email}`} style={{ color: theme.palette.text.secondary }}>
                {row.contact.email ?? '-'}
              </a>
            )
              : '--'
            }
          </Typography>

          {
            (row.contact.phone) && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <a href={`tel:${row.contact.phone}`} style={{ color: theme.palette.text.secondary }}>
                  {row.contact.phone ?? '-'}
                </a>
              </Typography>
            )
          }

          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
            <Tooltip
              title={
                row?.contact.source === ContactSource.JOB_INTERACTION
                  ? t('contacts.source.tooltip_job_interaction')
                  : t('contacts.source.tooltip_manual')
              }
            >
              <Label
                variant="soft"
                color={row?.contact.source === ContactSource.JOB_INTERACTION ? 'success' : 'info'}
              >
                {t(`enums.contact_source.${row?.contact.source}`)} ({row.contact.type ?? '-'})
              </Label>
            </Tooltip>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={showNotesModal.onTrue} size="small">
                <Iconify icon="mdi:comments" />
              </IconButton>
              {onRemoveLink && (
                <IconButton onClick={confirm.onTrue} size="small">
                  <Iconify color="error" icon="eva:trash-2-outline" />
                </IconButton>
              )}

            </Stack>

          </Stack>
        </CardContent>

        <ContactsNotesModal
          threadId={row.contact.thread.id}
          open={showNotesModal.value}
          onClose={showNotesModal.onFalse}
        />

        {onRemoveLink && (
          <ConfirmDialog
            open={confirm.value}
            onClose={confirm.onFalse}
            title={t('common.delete')}
            content={
              <Typography variant="body2">
                {t('contacts.delete_modal.confirm_message')}
              </Typography>
            }
            action={
              <Button variant="contained" color="error" onClick={handleRemoveContact}>
                {t('common.delete')}
              </Button>
            }
          />
        )}
      </Card>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack direction="column" alignItems="start">
            <Stack direction="row" alignItems="center" >

              {
                row.type === EntityLinkType.PRIMARY && (
                  <Tooltip title={t('companies.profile.primary_contact.tooltip')} arrow placement="top">
                    <Iconify icon="solar:star-bold" sx={{ color: theme.palette.primary.main, mr: .5 }} />
                  </Tooltip>
                )
              }

              <Link
                href={getViewDetailPath(row)}
                component={RouterLink}
                sx={{ verticalAlign: 'middle' }}
              >
                <TruncatedTextField
                  text={`${row.contact.first_name} ${row.contact.last_name ?? ''}`}
                  mode="characters"
                  limit={25}
                  showMoreEnabled={false}
                  variant="body2"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 1, textTransform: 'capitalize', color: theme.palette.text.primary }}
                />
              </Link>

            </Stack>

            {
              (row.contact.account && row.contact.account.organization) && (
                <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
                  {row.contact.account.organization.company_name}
                </Typography>
              )
            }
          </Stack>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {
              row.contact.email ? (
                <a href={`mailto:${row.contact.email}`} style={{ color: theme.palette.text.secondary }}>
                  {row.contact.email ?? '-'}
                </a>
              )
                : '--'
            }
          </Typography>
        </TableCell>

        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.contact.phone ? (
              <a href={`tel:${row.contact.phone}`} style={{ color: theme.palette.text.secondary }}>
                {row.contact.phone ?? '-'}
              </a>
            )
              : '--'
            }
          </Typography>
        </TableCell>

        <TableCell>
          <Tooltip
            title={
              row?.contact.source === ContactSource.JOB_INTERACTION
                ? t('contacts.source.tooltip_job_interaction')
                : t('contacts.source.tooltip_manual')
            }
          >
            <Label
              variant="soft"
              color={row?.contact.source === ContactSource.JOB_INTERACTION ? 'success' : 'info'}
            >
              {t(`enums.contact_source.${row?.contact.source}`)}
            </Label>
          </Tooltip>
        </TableCell>

        <TableCell sx={{ textTransform: 'capitalize' }}>{row.type ?? '-'}</TableCell>

        <TableCell align="right" sx={{ px: 4 }}>
          <Stack direction="row" justifyContent="end" spacing={1}>
            <Button variant="contained" size="small" onClick={showNotesModal.onTrue}>
              View Notes
            </Button>
            {
              onRemoveLink && (
                <IconButton onClick={confirm.onTrue}>
                  <Iconify color="red" icon="eva:trash-2-outline" />
                </IconButton>
              )
            }
          </Stack>
        </TableCell>

      </TableRow>

      <ContactsNotesModal
        threadId={row.contact.thread.id}
        open={showNotesModal.value}
        onClose={showNotesModal.onFalse}
      />

      {
        onRemoveLink && (
          <ConfirmDialog
            open={confirm.value}
            onClose={confirm.onFalse}
            title={t('common.remove')}
            content={
              <Typography variant="body2">
                {t('companies.profile.linked_contacts.unlink_confirm_message')}
              </Typography>
            }
            action={
              <Button variant="contained" color="error" onClick={handleRemoveContact}>
                {t('common.remove')}
              </Button>
            }
          />
        )
      }
    </>
  );
}
